import {
  FilterParams,
  PaginatedData,
  PaginatedParams
} from "src/library/utils";
import { IOrder } from "src/core/orders/api/orders.api";
import {
  EndpointConfig,
  RequestConfig,
  UseApiRequest
} from "src/core/shared/api/config.api";
import useAxios from "src/core/shared/hooks/useAxios";
import { IProduct } from "src/core/products/api/products.api";

// Entity types

export interface IProductSubstitution {
  id: number;
  startDate: string;
  endDate: string;
  previousProductId: number;
  previousProduct?: IProduct;
  replacementProductId: number;
  replacementProduct: IProduct;
  message: string;
  reasonCodeId: number;
  notifyUsers: boolean;
  reasonCode?: SubstitutionReasonCodeAttributes;
  status: ProductSubstitutionStatus;
}

export interface SubstitutionReasonCodeAttributes {
  id: number;
  code: string;
  description: string;
}

export enum ProductSubstitutionStatus {
  "Active" = "active",
  "Pending" = "pending",
  "Deleted" = "deleted",
  "Failed" = "failed",
  "DeleteFailed" = "delete_failed"
}

// request + response types

export type CreateSubstitutionParams = Pick<
  IProductSubstitution,
  "previousProductId" | "replacementProductId" | "startDate" | "endDate" | "reasonCodeId" | "message" | "notifyUsers"
>;

export interface GetSubstitutionParams {
  substitutionId: number;
}

export interface ExportSubstitutionsParams {
  name: string;
}

export interface UpdateSubstitutionParams {
  substitutionId: number;
  update: Partial<IProductSubstitution>;
}

export type DeleteSubstitutionParams = GetSubstitutionParams;

export interface StringResponse {
  message: string;
}

export interface ListSubstitutionsParams {
  pagination: PaginatedParams<IProductSubstitution>;
  filters: FilterParams<IProductSubstitution>;
}
export type ListSubstitutionsResponse = PaginatedData<IProductSubstitution>;

export interface ListOrdersBySubstitutionParams {
  substitutionId: number;
  pagination: PaginatedParams<IOrder>;
  filters: FilterParams<IOrder>;
}

export type ListOrdersBySubstitutionResponse = PaginatedData<IOrder>;

export type GetReasonCodesResponse = SubstitutionReasonCodeAttributes[];

export type DownloadReportResponse = {
  url: string;
};

export type ExportSubstitutionsResponse = {
  message: string;
};

type SubstitutionEndpointConfig = {
  CREATE_SUBSTITUTION: EndpointConfig<
    CreateSubstitutionParams,
    IProductSubstitution
  >;
  GET_SUBSTITUTION: EndpointConfig<
    GetSubstitutionParams,
    IProductSubstitution
  >;
  UPDATE_SUBSTITUTION: EndpointConfig<
    UpdateSubstitutionParams,
    IProductSubstitution
  >;
  DELETE_SUBSTITUTION: EndpointConfig<
    DeleteSubstitutionParams,
    StringResponse
  >;
  LIST_SUBSTITUTIONS: EndpointConfig<
    ListSubstitutionsParams,
    ListSubstitutionsResponse
  >;
  LIST_ORDERS_BY_SUBSTITUTION: EndpointConfig<
    ListOrdersBySubstitutionParams,
    ListOrdersBySubstitutionResponse
  >;
  GET_REASON_CODES: EndpointConfig<
    Record<string, never>,
    GetReasonCodesResponse
  >;
  DOWNLOAD_REPORT: EndpointConfig<
    GetSubstitutionParams,
    DownloadReportResponse
  >;
  EXPORT_SUBSTITUTIONS: EndpointConfig<
    ExportSubstitutionsParams,
    ExportSubstitutionsResponse
  >;
};

// substitution api specific logic

const BASE_URL = `${process.env.REACT_APP_API_URL}/substitution`;

const requests: RequestConfig<SubstitutionEndpointConfig> = {
  CREATE_SUBSTITUTION: (
    params => ({
      url: `${BASE_URL}/create`,
      method: "POST",
      data: params
    })
  ),
  GET_SUBSTITUTION: (
    params => ({
      url: `${BASE_URL}/${params.substitutionId}`,
      method: "GET"
    })
  ),
  UPDATE_SUBSTITUTION: (
    params => ({
      url: `${BASE_URL}/${params.substitutionId}`,
      method: "PATCH",
      data: params.update
    })
  ),
  DELETE_SUBSTITUTION: (
    params => ({
      url: `${BASE_URL}/${params.substitutionId}`,
      method: "DELETE"
    })
  ),
  LIST_SUBSTITUTIONS: (
    params => ({
      url: `${BASE_URL}/all`,
      method: "GET",
      params
    })
  ),
  LIST_ORDERS_BY_SUBSTITUTION: (
    params => ({
      url: `${BASE_URL}/${params.substitutionId}/orders`,
      method: "GET",
      params
    })
  ),
  GET_REASON_CODES: (
    () => ({
      url: `${BASE_URL}/reason-codes/all`,
      method: "GET"
    })
  ),
  DOWNLOAD_REPORT: (
    params => ({
      url: `${BASE_URL}/${params.substitutionId}/report`,
      method: "GET"
    })
  ),
  EXPORT_SUBSTITUTIONS: (
    params => ({
      url: `${BASE_URL}/export/all`,
      method: "GET",
      params
    })
  )
};

export const useSubstitutionsApi: UseApiRequest<SubstitutionEndpointConfig> = name => useAxios(requests[ name ], name);
